import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import { exhaustMap } from 'rxjs';
import { BaseState, RanneyWell, RanneyWellFlow, RanneyWellFlowRequest } from '../../../model';
import { RanneyWellService } from '../../../services';

export function withFlowMethods() {
  return signalStoreFeature(
    { state: type<BaseState<RanneyWell>>() },
    withMethods(store => {
      const service = inject(RanneyWellService);
      const addFlow = rxMethod<{ id: number; data: RanneyWellFlowRequest }>(
        exhaustMap(({ id, data }) =>
          service.addFlow(id, data).pipe(
            tapResponse({
              next: rsp =>
                patchState(store, state => {
                  const active = cloneDeep(state.active);
                  if (active && active.id === id) {
                    if (active.ranneyWellFlows) {
                      active.ranneyWellFlows.push(rsp);
                    } else {
                      active.ranneyWellFlows = [rsp];
                    }
                  }
                  return { active };
                }),
              error: console.log,
            })
          )
        )
      );
      const deleteFlow = rxMethod<{ id: number; flowId: number }>(
        exhaustMap(({ id, flowId }) =>
          service.deleteFlow(flowId).pipe(
            tapResponse({
              next: () =>
                patchState(store, state => {
                  const active = cloneDeep(state.active);
                  if (active && active.id === id) {
                    active.ranneyWellFlows = active.ranneyWellFlows.filter(flow => flow.id !== flowId);
                  }
                  return { active };
                }),
              error: console.log,
            })
          )
        )
      );
      const editFlow = rxMethod<{ flowId: number; data: RanneyWellFlowRequest }>(
        exhaustMap(({ flowId, data }) =>
          service.editFlow(flowId, data).pipe(
            tapResponse({
              next: rsp => updateActiveFlow(rsp),
              error: console.log,
            })
          )
        )
      );
      const updateActiveFlow = (data: RanneyWellFlow) =>
        patchState(store, state => {
          const active = cloneDeep(state.active);
          if (active && active.id === data.ranneyWellId) {
            const flowIdx = active.ranneyWellFlows.findIndex(flow => flow.id === data.id);
            if (flowIdx !== undefined && flowIdx !== -1) {
              active.ranneyWellFlows[flowIdx] = { ...data };
            }
          }
          return { active };
        });

      return { addFlow, editFlow, deleteFlow };
    })
  );
}
